// /var/www/aesirerp_lp/frontend/src/app/page.tsx
"use client";

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ScrollAnimator } from '@/components/ScrollAnimator';
import styles from './Page.module.css';
import { ParticleBackground } from '@/components/ParticleBackground';
import { 
  ShoppingCart, DollarSign, Users, MessageSquare, 
  Target, Check, Copy, HandCoins, ArrowRight, 
  FileText, MonitorSmartphone, Banknote, Warehouse, 
  Printer, ArrowLeftRight, Cog, Wrench, Layers,
  ShoppingBag, Bot, Zap, Smartphone, XCircle, CheckCircle2, Plus, Phone
} from 'lucide-react';

export default function Home() {
  const [activeTab, setActiveTab] = useState('vendas');

  const handleCopy = () => {
    navigator.clipboard.writeText("AESIR-VIP-2026");
    alert("Código copiado!");
  };

  return (
    <>
      {/* ===== HERO SECTION (COREOGRAFADA) ===== */}
      <section id="inicio" className={styles.hero}>
        <ParticleBackground />
        <div className={styles.container}>
          <div className={styles.heroContent}>
            
            {/* 1. TÍTULO: Mais direto e poderoso */}
            <ScrollAnimator>
              <h1 className={styles.heroTitle}>
                Gestão Empresarial Unificada <br />
                <span>& Automação Inteligente.</span>
              </h1>
            </ScrollAnimator>

            {/* 2. TEXTO: Descritivo (Aprova no Google) e Vendedor (Converte Lead) */}
            <ScrollAnimator delay={200}>
              <p className={styles.heroDescription}>
                O <strong>Aesir ERP</strong> é uma plataforma SaaS modular que integra Vendas, Financeiro, RH e Marketing no WhatsApp. 
                Emita notas fiscais, controle estoques e <strong>sincronize sua agenda Google</strong> em um único painel. 
                Contrate apenas o que sua empresa precisa.
              </p>
            </ScrollAnimator>

            {/* 3. BOTÕES (Entram 400ms depois) */}
            <ScrollAnimator delay={400}>
              <div className={styles.heroActions}>
                <Link href="https://aesirerp.com/register" className={`${styles.button} ${styles.primary}`}>
                  Começar Agora
                  <ArrowRight size={20} />
                </Link>
                <a href="#modulos" className={`${styles.button} ${styles.secondary}`}>
                  <Layers size={20} />
                  Ver Funcionalidades
                </a>
              </div>
            </ScrollAnimator>

            {/* 4. TRUST BADGES (Entram 600ms depois) */}
            <ScrollAnimator delay={600}>
              <div className={styles.trustBadges}>
                <p>Tecnologia Certificada</p>
                <div className={styles.badgesRow}>
                   <div className={styles.badgeItem}>
                     <Image src="/assets/images/metapartner2.png" alt="Meta Business Partner" width={140} height={40} style={{objectFit: 'contain'}} />
                   </div>
                   <div className={styles.separator}></div>
                   <div className={styles.badgeItem}>
                     <Image src="/assets/images/googlepartner2.png" alt="Google Partner" width={140} height={50} style={{objectFit: 'contain'}} />
                   </div>
                </div>
              </div>
            </ScrollAnimator>

          </div>
        </div>
      </section>

      {/* ===== SOLUÇÕES MODULARES (CARDS) ===== */}
      <section id="modulos" className={`${styles.section} ${styles.interactiveSection}`}>
        <div className={styles.container}>
          <div className={styles.sectionTitle}>
            <h2>Modularidade Total</h2>
            <p>Contrate apenas o que faz sentido para você</p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
            <div className={styles.tabs}>
              <button
                className={`${styles.tabButton} ${activeTab === 'vendas' ? styles.active : ''}`}
                onClick={() => setActiveTab('vendas')}
              >
                <ShoppingCart size={18} />
                Vendas & PDV
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'marketing' ? styles.active : ''}`}
                onClick={() => setActiveTab('marketing')}
              >
                <Target size={18} />
                Campanhas & Chat
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'gestao' ? styles.active : ''}`}
                onClick={() => setActiveTab('gestao')}
              >
                <DollarSign size={18} />
                Financeiro & RH
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'servicos' ? styles.active : ''}`}
                onClick={() => setActiveTab('servicos')}
              >
                <Wrench size={18} />
                Serviços (OS)
              </button>
            </div>
          </div>

          <ScrollAnimator>
            <div className={styles.tabContent}>
              
              {/* === Módulo de VENDAS === */}
              {activeTab === 'vendas' && (
                <div className={styles.contentWrapper}>
                  <div className={styles.textContent}>
                    <span className={styles.moduleBadge}>Módulo 1</span>
                    <h3>Vendas e PDV Completo</h3>
                    <p>Agilidade extrema no balcão ou na internet. Um sistema robusto para quem não pode parar de vender.</p>
                    
                    <div className={styles.featuresGrid}>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><MonitorSmartphone size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>PDV Frente de Caixa</h4>
                          <p>Emissão de NFC-e rápida e funciona offline.</p>
                        </div>
                      </div>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><Warehouse size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>Controle de Estoque</h4>
                          <p>Baixa automática e gestão de grade/cor.</p>
                        </div>
                      </div>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><ShoppingBag size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>Catálogo & iFood</h4>
                          <p>Venda online com integração nativa.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.imageContainer}>
                    <Image src="/assets/images/vendas.png" alt="PDV Aesir" width={600} height={450} className={styles.tabImage} />
                  </div>
                </div>
              )}

              {/* === Módulo de MARKETING === */}
              {activeTab === 'marketing' && (
                <div className={styles.contentWrapper}>
                   <div className={styles.textContent}>
                    <span className={styles.moduleBadge}>Módulo 2 e 8</span>
                    <h3>Grupos Massivos & Chat IA</h3>
                    <p>A ferramenta secreta de <strong>Influencers e Lançamentos</strong>. Controle sua audiência e automatize o atendimento.</p>
                    
                    <div className={styles.featuresGrid}>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><Users size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>Gestão de Grupos</h4>
                          <p>Adicione/remova leads e agende mensagens.</p>
                        </div>
                      </div>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><MessageSquare size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>Aesir Chat Omnichannel</h4>
                          <p>Centralize WhatsApp, Instagram e Site.</p>
                        </div>
                      </div>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><Bot size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>Agentes de IA</h4>
                          <p>Múltiplos atendentes virtuais 24/7.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.imageContainer}>
                    <Image src="/assets/images/campanhas.png" alt="Marketing Aesir" width={600} height={450} className={styles.tabImage} />
                  </div>
                </div>
              )}

              {/* === Módulo de FINANCEIRO === */}
              {activeTab === 'gestao' && (
                <div className={styles.contentWrapper}>
                   <div className={styles.textContent}>
                    <span className={styles.moduleBadge}>Módulo 3 e 4</span>
                    <h3>Financeiro e RH</h3>
                    <p>Domine os números. Organize o fluxo de caixa, emita notas e controle sua equipe em um só lugar.</p>
                    
                    <div className={styles.featuresGrid}>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><Banknote size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>Fluxo de Caixa</h4>
                          <p>Contas a pagar, receber e DRE gerencial.</p>
                        </div>
                      </div>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><FileText size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>Emissor Fiscal</h4>
                          <p>NFe, NFCe e NFSe ilimitadas.</p>
                        </div>
                      </div>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><Users size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>RH & Ponto</h4>
                          <p>Controle de funcionários e folha.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.imageContainer}>
                    <Image src="/assets/images/financeiro.png" alt="Financeiro Aesir" width={600} height={450} className={styles.tabImage} />
                  </div>
                </div>
              )}

               {/* === Módulo de SERVIÇOS === */}
               {activeTab === 'servicos' && (
                <div className={styles.contentWrapper}>
                   <div className={styles.textContent}>
                    <span className={styles.moduleBadge}>Módulo 5</span>
                    <h3>Ordem de Serviço (OS)</h3>
                    <p>O braço direito de assistências técnicas e prestadores de serviço.</p>
                    
                    <div className={styles.featuresGrid}>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><Wrench size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>Status de OS</h4>
                          <p>Acompanhamento em tempo real para o cliente.</p>
                        </div>
                      </div>
                      <div className={styles.featureCard}>
                        <div className={styles.featureIconBox}><Printer size={24} /></div>
                        <div className={styles.featureText}>
                          <h4>Impressão de Laudos</h4>
                          <p>Garantias e checklists personalizados.</p>
                        </div>
                      </div>
                      <div className={styles.featureCard}>
                         <div className={styles.featureIconBox}><Cog size={24} /></div>
                         <div className={styles.featureText}>
                          <h4>Peças e Serviços</h4>
                          <p>Controle de mão de obra e estoque.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.imageContainer}>
                    <Image src="/assets/images/os.png" alt="OS Aesir" width={600} height={450} className={styles.tabImage} />
                  </div>
                </div>
              )}

            </div>
          </ScrollAnimator>
        </div>
      </section>

      {/* ===== COMPARAÇÃO (COMBO: ERP + MARKETING/IA) ===== */}
      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.sectionTitle}>
            <h2>Evolução da Gestão</h2>
            <p>Por que mudar para o Aesir?</p>
          </div>

          <ScrollAnimator>
            <div className={styles.comparisonGrid}>
              
              {/* O VELHO */}
              <div className={`${styles.comparisonCard} ${styles.cardBad}`}>
                <div className={styles.cardHeader}>
                  <h3>ERP Tradicional</h3>
                  <span>O passado</span>
                </div>
                <ul className={styles.comparisonList}>
                  <li className={styles.badItem}>
                    <XCircle size={20} />
                    <span><strong>Venda Casada:</strong> Você paga caro por módulos que nunca usa.</span>
                  </li>
                  <li className={styles.badItem}>
                    <XCircle size={20} />
                    <span><strong>Interface Antiga:</strong> Telas cinzas, complexas e difíceis de treinar.</span>
                  </li>
                  <li className={styles.badItem}>
                    <XCircle size={20} />
                    <span><strong>Gestão Manual:</strong> Grupos de lançamento controlados em planilhas.</span>
                  </li>
                  <li className={styles.badItem}>
                    <XCircle size={20} />
                    <span><strong>Atendimento Limitado:</strong> Um celular por vendedor e sem IA.</span>
                  </li>
                   <li className={styles.badItem}>
                    <XCircle size={20} />
                    <span><strong>Preso no PC:</strong> Precisa instalar (exe) e não roda no celular.</span>
                  </li>
                </ul>
              </div>

              {/* O NOVO */}
              <div className={`${styles.comparisonCard} ${styles.cardGood}`}>
                <div className={styles.popularBadge}>Recomendado</div>
                <div className={styles.cardHeader}>
                  <h3>Aesir ERP</h3>
                  <span>O futuro</span>
                </div>
                <ul className={styles.comparisonList}>
                  <li className={styles.goodItem}>
                    <CheckCircle2 size={20} />
                    <span><strong>100% Modular:</strong> Monte seu plano ideal e economize dinheiro.</span>
                  </li>
                  <li className={styles.goodItem}>
                    <Zap size={20} />
                    <span><strong>Material You 3:</strong> Design Google, bonito e fácil de usar.</span>
                  </li>
                  <li className={styles.goodItem}>
                    <Users size={20} />
                    <span><strong>Grupos Massivos:</strong> Adicione/remova pessoas e agende disparos.</span>
                  </li>
                  <li className={styles.goodItem}>
                    <Bot size={20} />
                    <span><strong>IA & Omnichannel:</strong> Agentes inteligentes atendendo 24/7.</span>
                  </li>
                  <li className={styles.goodItem}>
                    <Smartphone size={20} />
                    <span><strong>Nuvem & Mobile:</strong> Controle sua empresa pelo celular.</span>
                  </li>
                </ul>
              </div>
            </div>
          </ScrollAnimator>
        </div>
      </section>

      {/* ===== ECOSSISTEMA ===== */}
      <section className={styles.section} style={{background: 'var(--md-sys-color-surface-variant)'}}>
        <div className={styles.container}>
            <ScrollAnimator>
            <div className={styles.sectionTitle}>
                <h2>Ecossistema Completo</h2>
                <p>Todos os módulos à sua disposição</p>
            </div>
            </ScrollAnimator>
            
            <div className={styles.chipGrid}>
              {[
                { name: 'Vendas (PDV)', icon: <ShoppingCart size={18} /> },
                { name: 'Campanhas Whats', icon: <Target size={18} /> },
                { name: 'Financeiro', icon: <Banknote size={18} /> },
                { name: 'Recursos Humanos', icon: <Users size={18} /> },
                { name: 'Ordem de Serviço', icon: <Wrench size={18} /> },
                { name: 'Aesir Chat Omni', icon: <MessageSquare size={18} /> },
                { name: 'Comunidades', icon: <Users size={18} /> },
                { name: 'Nota Fiscal', icon: <FileText size={18} /> },
                { name: 'Estoque', icon: <Warehouse size={18} /> },
                { name: 'Integrações', icon: <ArrowLeftRight size={18} /> },
                { name: 'Automações', icon: <Cog size={18} /> },
                { name: 'Multi-lojas', icon: <ShoppingCart size={18} /> },
              ].map((feature, idx) => (
                <ScrollAnimator key={idx} delay={idx * 50}>
                  <div className={styles.chip}>
                    <div className={styles.chipIcon}>{feature.icon}</div>
                    <span>{feature.name}</span>
                  </div>
                </ScrollAnimator>
              ))}
            </div>

            <ScrollAnimator delay={600}>
              <div style={{textAlign: 'center', marginTop: '3rem'}}>
                  <p style={{fontSize: '1.1rem', marginBottom: '1rem', color: 'var(--md-sys-color-on-surface-variant)'}}>
                      E muito mais para uma gestão inteligente e acessível.
                  </p>
              </div>
            </ScrollAnimator>
        </div>
      </section>

      {/* ===== INTEGRAÇÕES ===== */}
      <section className={styles.section} style={{padding: '3rem 0'}}>
        <div className={styles.container}>
          <p style={{textAlign: 'center', marginBottom: '2rem', color: 'var(--md-sys-color-on-surface-variant)', fontWeight: '600', textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '2px'}}>
            Integrado com as melhores ferramentas do mercado
          </p>
          <ScrollAnimator>
            <div style={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '3rem', alignItems: 'center', opacity: 0.7}}>
              <h3 style={{fontWeight: 800, color: '#EA1D2C', fontSize: '1.5rem'}}>iFood</h3>
              <h3 style={{fontWeight: 800, color: '#1877F2', fontSize: '1.5rem'}}>Meta</h3>
              <h3 style={{fontWeight: 800, color: '#25D366', fontSize: '1.5rem'}}>WhatsApp</h3>
              <h3 style={{fontWeight: 800, color: '#2CB3F2', fontSize: '1.5rem'}}>Melhor Envio</h3>
              <h3 style={{fontWeight: 800, color: '#4F46E5', fontSize: '1.5rem'}}>Chatwoot</h3>
              <h3 style={{fontWeight: 800, color: '#009E7F', fontSize: '1.5rem'}}>Mercado Livre</h3>
            </div>
          </ScrollAnimator>
        </div>
      </section>

      {/* ===== INDIQUE E GANHE ===== */}
      <section id="parceiros" className={styles.section}>
        <div className={styles.container}>
          
          <div className={styles.referralSection}>
            <div className={styles.referralContainer}>
              
              <ScrollAnimator>
                <div className={styles.referralHeader}>
                  <div className={styles.headerIcon}>
                    <HandCoins size={40} />
                  </div>
                  <h1 className={styles.heroTitle} style={{fontSize: '2.5rem', marginBottom: '1rem'}}>Programa de Indicações</h1>
                  <p className={styles.heroDescription}>Indique o Aesir ERP e crie uma fonte de renda recorrente.</p>
                </div>
              </ScrollAnimator>

              {/* DASHBOARD */}
              <ScrollAnimator>
                <div className={styles.dashboardGrid}>
                  
                  {/* Card Esquerdo */}
                  <div className={styles.m3Card}>
                    <div className={styles.cardContent}>
                      <div className={styles.codeSection}>
                        <span className="text-xs font-bold text-orange-600 bg-orange-100 px-3 py-1 rounded-full uppercase tracking-wider" style={{color: 'var(--md-sys-color-primary)', background: 'var(--md-sys-color-primary-container)'}}>
                          Painel do Parceiro
                        </span>
                        <h4>Compartilhe e Ganhe</h4>
                        <p className="text-gray-500 text-sm mb-4">Envie este código para seus indicados e garanta sua comissão.</p>
                        
                        <div className={styles.codePill}>
                          <div className={styles.codeText}>AESIR-VIP-2026</div>
                          <button className={styles.copyBtn} onClick={handleCopy} title="Copiar código">
                            <Copy size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lado Direito: Passos */}
                  <div className={styles.stepsContainer}>
                    <div className={styles.stepItem}>
                      <div className={styles.stepBadge}>1</div>
                      <div className={styles.stepInfo}>
                        <h3>Você Indica</h3>
                        <p className="text-gray-500 text-sm">Compartilhe seu código exclusivo.</p>
                      </div>
                    </div>
                    <div className={styles.stepLine}></div>
                    <div className={styles.stepItem}>
                      <div className={styles.stepBadge}>2</div>
                      <div className={styles.stepInfo}>
                        <h3>Eles Assinam</h3>
                        <p className="text-gray-500 text-sm">O cliente ganha desconto e contrata.</p>
                      </div>
                    </div>
                    <div className={styles.stepLine}></div>
                    <div className={styles.stepItem}>
                      <div className={styles.stepBadge}>3</div>
                      <div className={styles.stepInfo}>
                        <h3>Você Ganha</h3>
                        <p className="text-gray-500 text-sm">Receba <strong>20% recorrente</strong> todo mês.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollAnimator>

              {/* WHITELABEL */}
              <ScrollAnimator>
                <div className={styles.whitelabelPromo}>
                  <div className={styles.wlContent}>
                    <span className={styles.wlBadge}>Oportunidade Premium</span>
                    <h2>Torne-se um Revendedor White Label</h2>
                    <p style={{color: '#a3a3a3', lineHeight: '1.6'}}>
                      Leve sua marca para o próximo nível. Tenha o <strong>Aesir ERP com a SUA MARCA</strong>, seu domínio e defina seus preços.
                    </p>
                    <ul className={styles.wlBenefitsList}>
                      <li><Check size={18} className="text-yellow-400" /> Sua marca e logo</li>
                      <li><Check size={18} className="text-yellow-400" /> Domínio próprio</li>
                      <li><Check size={18} className="text-yellow-400" /> Painel exclusivo</li>
                    </ul>
                  </div>
                  <div className={styles.wlAction}>
                    <a href="https://api.whatsapp.com/send?phone=5511983493522&text=Ola,%20quero%20saber%20sobre%20o%20Whitelabel" target="_blank" className={styles.whatsappBtn}>
                      <Phone size={24} />
                      Quero ser White Label
                    </a>
                  </div>
                </div>
              </ScrollAnimator>

            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className={styles.section}>
        <div className={styles.container} style={{maxWidth: '800px'}}>
          <div className={styles.sectionTitle}>
            <h2>Dúvidas Comuns</h2>
            <p>Tudo o que você precisa saber</p>
          </div>

          <ScrollAnimator>
            <div className={styles.faqGrid}>
              {[
                { 
                  p: "Posso contratar apenas o módulo de WhatsApp?", 
                  r: "Sim! Com a nossa estrutura modular, você não é obrigado a levar o ERP completo. Se o seu foco é marketing e atendimento, ative apenas o módulo de Campanhas e Chat." 
                },
                { 
                  p: "Como funciona a cobrança recorrente?", 
                  r: "Trabalhamos com assinatura mensal via cartão ou PIX. Sem fidelidade forçada. Você paga enquanto usar e pode cancelar ou alterar seus módulos a qualquer momento." 
                },
                { 
                  p: "O sistema emite Nota Fiscal para todo o Brasil?", 
                  r: "Sim, o módulo Financeiro emite NFe, NFCe e NFS-e, com cálculo automático de impostos e integração com a contabilidade." 
                },
                { 
                  p: "Posso revender o Aesir com a minha marca?", 
                  r: "Com certeza. Temos um programa Whitelabel exclusivo onde você personaliza logo, cores e domínio, e define seu próprio preço para seus clientes." 
                }
              ].map((item, index) => (
                <details key={index} className={styles.faqItem}>
                  <summary className={styles.faqSummary}>
                    {item.p}
                    <Plus className={styles.faqIcon} size={20} />
                  </summary>
                  <div className={styles.faqContent}>
                    <p>{item.r}</p>
                  </div>
                </details>
              ))}
            </div>
          </ScrollAnimator>
        </div>
      </section>

      {/* ===== DEPOIMENTOS ===== */}
      <section id="depoimentos" className={styles.section} style={{background: 'var(--md-sys-color-surface-variant)'}}>
        <div className={styles.container}>
          <div className={styles.sectionTitle}>
            <h2>Depoimentos</h2>
            <p>Quem usa, recomenda</p>
          </div>
          <ScrollAnimator>
            <div className={styles.bentoGrid}>
              {[
                { name: "Grupo Digital", quote: "Otimizamos nossas vendas com total eficiência.", avatar: "/assets/images/dep1.png" },
                { name: "RMaia Soluções", quote: "Desde que adotamos, o gerenciamento ficou mais ágil.", avatar: "/assets/images/dep2.png" },
                { name: "Grupo Safira", quote: "O sistema de disparos massivos revolucionou nossa comunicação.", avatar: "/assets/images/dep3.png" },
              ].map((testimonial, i) => (
                <div key={i} className={`${styles.bentoCard} ${styles.colSpan2}`} style={{background: 'white'}}>
                  <div className="flex items-center gap-4 mb-4" style={{display:'flex', alignItems:'center', gap:'1rem', marginBottom:'1rem'}}>
                    <div style={{borderRadius:'50%', overflow:'hidden', width:'48px', height:'48px'}}>
                       <Image src={testimonial.avatar} alt={testimonial.name} width={48} height={48} />
                    </div>
                    <div>
                      <h4 style={{fontWeight:'700', color:'var(--md-sys-color-on-surface)'}}>{testimonial.name}</h4>
                      <div style={{color:'#facc15', fontSize:'0.8rem'}}>★★★★★</div>
                    </div>
                  </div>
                  <p style={{fontStyle:'italic', color:'var(--md-sys-color-on-surface-variant)'}}>"{testimonial.quote}"</p>
                </div>
              ))}
            </div>
          </ScrollAnimator>
        </div>
      </section>
    </>
  );
}